import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { RollAndAdvanceLogic } from '../logic/board/RollAndAdvanceLogic';
import type { BoardLogicContext } from '../logic/board/BoardLogicContext';
import { BoardModel } from '../model/board/BoardModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { BoardWidget } from '../widgets/board/BoardWidget';
import { BoardHudComponent } from './components/BoardHudComponent';
import { DEFAULT_BOARD_TILES } from './fixtures/boardDemoFixtures';
import { DEFAULT_PICK_EM_PRIZES } from './fixtures/pickEmFixtures';
import { PickEmFragment } from './fragments/PickEmFragment';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 500;

/**
 * Board-game demo — Phase E foundation. A 12-tile snake path with cash,
 * clue, location, bonus, and finish tiles. Tap ROLL (or press space) to
 * roll a d6 and hop forward; landing on a tile credits its reward.
 *
 * <p>Lap-completion (landing on the finish tile) currently just resets
 * position to the start with the {@code SOLVED} reward credited; the
 * MMS-shaped variant in Phase F will trigger a free-spin bonus on the
 * companion slot scene instead.
 */
export class BoardDemoScene extends SceneBase {
  private board: BoardWidget | null = null;
  private hud: BoardHudComponent | null = null;
  private pickEmFragment: PickEmFragment | null = null;
  private logic: RollAndAdvanceLogic | null = null;
  private rolling = false;
  private readonly random: Random;
  private readonly model: BoardModel;
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.model = new BoardModel({
      tiles: DEFAULT_BOARD_TILES,
      startingBalance: profile?.balance ?? STARTING_BALANCE,
    });
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected buildContent(): void {
    this.board = new BoardWidget({
      tiles: DEFAULT_BOARD_TILES,
      tileSize: 76,
      tileSpacing: 8,
      tilesPerRow: 4,
      accentColor: this.theme.palette.accent,
      token: { fillColor: this.theme.palette.accent },
    });
    this.app.stage.addChild(this.board);

    this.hud = new BoardHudComponent(this.model, {
      onRoll: () => void this.handleRoll(),
    });
    this.app.stage.addChild(this.hud);

    // Pick-Em fragment for bonus tiles. Sits on top of the board / HUD
    // so when a bonus tile triggers it, everything dims behind.
    this.pickEmFragment = new PickEmFragment(
      {
        accentColor: this.theme.palette.accent,
        warmColor: this.theme.palette.warm,
      },
      this.random,
    );
    this.app.stage.addChild(this.pickEmFragment);

    const ctx: BoardLogicContext = {
      model: this.model,
      widget: this.board,
      random: this.random,
    };
    this.logic = new RollAndAdvanceLogic(ctx);

    // Bonus tiles trigger a Pick-Em mini-bonus. The tile already pays its
    // base reward; the Pick-Em is an *additional* payout layered on top —
    // landing on a bonus tile is meant to feel like the prize-of-prizes.
    this.model.events.on('tile-activated', (event) => {
      if (event.tile.kind === 'bonus') {
        void this.runBonusPickEm();
      }
    });

    // Reset to start whenever we hit the goal — gives the player another
    // lap. Logic still emits the goal-reached event for celebration.
    this.model.events.on('goal-reached', () => {
      // Defer so the goal-tile activation finishes before we snap back.
      setTimeout(() => {
        this.model.resetPosition();
        this.board?.setHighlightedIndex(0);
        void this.board?.hopTokenTo(0, 240);
      }, 800);
    });
  }

  private async runBonusPickEm(): Promise<void> {
    if (!this.pickEmFragment) return;
    const winnings = await this.pickEmFragment.present({
      title: '🎁 BONUS!',
      subtitle: 'Pick 3 boxes to reveal extra coins',
      prizes: DEFAULT_PICK_EM_PRIZES,
      boxCount: 9,
      picksAllowed: 3,
      columnCount: 3,
    });
    if (winnings > 0) {
      this.model.credit(winnings, 'session-grant');
    }
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      void this.handleRoll();
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    this.model.dispose();
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    this.pickEmFragment?.destroy();
    this.pickEmFragment = null;
    if (this.board) {
      this.board.destroyBoard();
      this.board = null;
    }
  }

  private layout(): void {
    if (!this.board || !this.hud) return;
    const { width, height } = this.app.renderer;
    const naturalWidth = this.board.totalWidth();
    const naturalHeight = this.board.totalHeight();
    const horizontalGutter = 24;
    const scale = Math.min(1, (width - horizontalGutter * 2) / naturalWidth);
    this.board.scale.set(scale);
    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;
    const boardX = Math.round((width - scaledWidth) / 2);
    const boardY = Math.round((height - scaledHeight) / 2 - 48 + 16);
    this.board.x = boardX;
    this.board.y = boardY;

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: boardY,
      machineBottom: boardY + scaledHeight,
      spinButtonY: Math.round(boardY + scaledHeight + 36),
    };
    this.hud.layout(ctx);
    this.pickEmFragment?.layout(ctx);
  }

  private async handleRoll(): Promise<void> {
    if (this.rolling || !this.hud || !this.logic) return;
    this.rolling = true;
    this.hud.setRolling(true);
    this.hud.setStatusText('🎲 rolling…');

    try {
      await this.logic.execute();
    } finally {
      this.rolling = false;
      this.hud.setRolling(false);
    }
  }
}
